import {
  ActivityType,
  BolusState,
} from "@tandemdiabetes/sugarmate-api-react-client"
import type { HTMLProps } from "react"
import { useMemo } from "react"
import { useGraphContext } from "../../GraphContext"
import type {
  GraphActivity,
  BolusGraphActivity,
  FavoriteGraphActivity,
  FoodGraphActivity,
  InsulinGraphActivity,
} from "./GraphActivity"
import styles from "./GraphActivityTitle.module.scss"
import { usePolyglot } from "src/contexts"
import { useEntryTemplate } from "src/hooks"
import {
  round,
  serverInsulinToUserInsulin,
} from "src/utils"

type Props<T extends GraphActivity> = HTMLProps<HTMLDivElement> & {
  activity: T;
}

export function GraphActivityTitle(props: Props<GraphActivity>) {
  const {
    activity,
    ...textProps
  } = props

  const { graphActivityHeight } = useGraphContext()

  const sharedProps = useMemo(
    () => ({
      style: { fontSize: graphActivityHeight / 2 },
      className: styles.title,
    }),
    [graphActivityHeight],
  )

  if (activity.entryTemplateId) {
    return (
      <FavoriteActivityTitle
        activity={activity}
        {...textProps}
        {...sharedProps}
      />
    )
  }

  switch (activity.activityType) {
    case ActivityType.Bolus:
      return (
        <BolusActivityTitle
          activity={activity}
          {...textProps}
          {...sharedProps}
        />
      )
    case ActivityType.Food:
      return (
        <FoodActivityTitle
          activity={activity}
          {...textProps}
          {...sharedProps}
        />
      )
    case ActivityType.Insulin:
      return (
        <InsulinActivityTitle
          activity={activity}
          {...textProps}
          {...sharedProps}
        />
      )
    default:
      return null
  }
}

function FavoriteActivityTitle(props: Props<FavoriteGraphActivity>) {
  const {
    activity,
    ...textProps
  } = props

  const entryTemplate = useEntryTemplate(activity.entryTemplateId)

  if (!entryTemplate) {
    return null
  }

  return (
    <div {...textProps}>
      {entryTemplate.title}
    </div>
  )
}

function BolusActivityTitle(props: Props<BolusGraphActivity>) {
  const {
    activity,
    ...textProps
  } = props

  const polyglot = usePolyglot()
  const languageTag = polyglot.locale()
  const insulinTotal = polyglot.t(
    "unit.num_units_abbreviated",
    {
      units: serverInsulinToUserInsulin(
        activity.bolus.state === BolusState.InProgress
          ? (activity.bolus.insulin_total_requested ?? activity.bolus.insulin_total_delivered ?? 0)
          : (activity.bolus.insulin_total_delivered ?? 0),
      ).toLocaleString(languageTag),
    },
  )

  return (
    <div {...textProps}>
      {insulinTotal}
    </div>
  )
}

function FoodActivityTitle(props: Props<FoodGraphActivity>) {
  const {
    activity,
    ...textProps
  } = props

  const polyglot = usePolyglot()

  if (!activity.food.carbs) {
    return null
  }

  const carbs = polyglot.t(
    "unit.num_grams",
    {
      grams: round(
        activity.food.carbs,
        1,
      ).toLocaleString(polyglot.locale()),
    },
  )

  return (
    <div {...textProps}>
      {carbs}
    </div>
  )
}

function InsulinActivityTitle(props: Props<InsulinGraphActivity>) {
  const {
    activity,
    ...textProps
  } = props

  const polyglot = usePolyglot()

  if (!activity.insulin.num_units) {
    return null
  }

  const units = polyglot.t(
    "unit.num_units_abbreviated",
    {
      units: round(
        activity.insulin.num_units,
        3,
      ).toLocaleString(polyglot.locale()),
    },
  )

  return (
    <div {...textProps}>
      {units}
    </div>
  )
}
